/**
 * Self-test suite for the voice-message feature.
 *
 * Five groups mirroring `kaether_for_ios/Stoat/E2EE/VoiceMessageSelfTest.swift`:
 *
 *   A. Metadata codec — prefix discrimination, range validation,
 *      schema/base64 rejection. Covers every other E2EE control-message
 *      prefix so a confused-deputy peer cannot smuggle a chat/envelope/
 *      receipt/tombstone/reply/pin/draft/skdm/skmsg/role/gmember/voip-call/
 *      call-link payload past the voice decoder.
 *   B. Waveform downsampling — deterministic, fixed-length output,
 *      handles empty/upscale/silence/constant edge cases.
 *   C. Silence ratio — pure float→ratio function feeding the
 *      auto-stop heuristic.
 *   D. Recording policy — priority matrix (paused > max > silence >
 *      continue), boundary checks.
 *   E. Playback-rate store — defaults, round-trip, per-channel isolation,
 *      persistence across store instances, resetAll.
 *
 * All IndexedDB cases use isolated database names so the suite is safe
 * to run on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  VoiceMessageMetadataCodec,
  VoiceMessageMetadataError,
  makeVoiceMessageMetadata,
  voiceMessageMetadataEqual,
} from "./VoiceMessageMetadata";
import { VoiceMessageWaveform } from "./VoiceMessageWaveform";
import { VoiceMessageRecordingPolicy } from "./VoiceMessageRecordingPolicy";
import { VoicePlaybackRateStore } from "./VoiceMessagePlaybackRatePolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Returns a VoicePlaybackRateStore backed by a fresh, isolated IDB database. */
function freshRateStore(): VoicePlaybackRateStore {
  dbCounter += 1;
  return new VoicePlaybackRateStore(
    `kaether:voice-rate:selftest:${dbCounter}:${Date.now()}`,
  );
}

/** base64url-encode a UTF-8 string (for crafting synthetic payloads in tests). */
function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

/** Build a valid 64-byte waveform for test fixture use. */
function makeWaveform(fill = 128): Uint8Array {
  return new Uint8Array(64).fill(fill);
}

/** Build a waveform where bucket[i] = (i * 3) % 256. Mirrors iOS round-trip test. */
function makeIndexedWaveform(): Uint8Array {
  const w = new Uint8Array(64);
  for (let i = 0; i < 64; i++) {
    w[i] = (i * 3) % 256;
  }
  return w;
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Voice messages (record + waveform + playback)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Metadata codec
        testCodecRoundTrip(),
        testIsVoiceMessageDiscriminatesAllPrefixes(),
        testDurationBelowMinRejected(),
        testDurationAboveMaxRejected(),
        testSampleRateNot16kRejected(),
        testBitRateOutOfRangeRejected(),
        testWaveformLengthRejected(),
        testUnsupportedSchemaRejected(),
        testMalformedBase64Rejected(),
        testDecodeSideValidationRejectsBadDuration(),

        // B. Waveform downsampling
        testWaveformEmptyInput(),
        testWaveformConstantInput(),
        testWaveformSineProducesNonDegenerate(),
        testWaveformBucketCountEnforced(),
        testWaveformUpscalesShortInput(),
        testWaveformDeterministic(),
        testWaveformKnownVector(),

        // C. Silence ratio
        testSilenceRatioAllZero(),
        testSilenceRatioAllLoud(),
        testSilenceRatioMixed(),

        // D. Recording policy
        testPolicyContinueBelowMax(),
        testPolicyForceStopAtMax(),
        testPolicyForceStopOnSilenceCap(),
        testPolicyUserPausedShortCircuits(),
        testPolicyUserPausedBeatsMaxDuration(),

        // E. Playback-rate store
        testRateDefaultIsX1(),
        testRateRoundTrip(),
        testRatePerChannelIsolation(),
        testRatePersistsAcrossInstances(),
        testRateResetAll(),
      ]),
    ),
});

// ---------------------------------------------------------------------------
// A. Metadata codec
// ---------------------------------------------------------------------------

async function testCodecRoundTrip(): Promise<TestCase> {
  const name = "codec round-trips voice metadata";
  try {
    const waveform = makeIndexedWaveform();
    const m = makeVoiceMessageMetadata({
      durationMillis: 4200,
      sampleRateHz: 16_000,
      bitRateBps: 32_000,
      waveformBuckets: waveform,
      recordedAt: 1_700_000_000,
    });
    const encoded = VoiceMessageMetadataCodec.encode(m);
    const decoded = VoiceMessageMetadataCodec.decode(encoded);
    if (!voiceMessageMetadataEqual(m, decoded)) {
      return fail(name, `round-trip mismatch: durationMillis=${decoded.durationMillis}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsVoiceMessageDiscriminatesAllPrefixes(): Promise<TestCase> {
  const name = "isVoiceMessage discriminates all sibling prefixes";
  try {
    const m = makeVoiceMessageMetadata({ durationMillis: 1000, waveformBuckets: makeWaveform() });
    const voiceEncoded = VoiceMessageMetadataCodec.encode(m);
    const positive = VoiceMessageMetadataCodec.isVoiceMessage(voiceEncoded);
    const negatives = [
      "hello there",
      "kaether:e2ee:v1:abc",
      "kaether:receipt:v1:abc",
      "kaether:tombstone:v1:abc",
      "kaether:reply:v1:abc",
      "kaether:pin:v1:abc",
      "kaether:draft:v1:abc",
      "kaether:skdm:v1:abc",
      "kaether:skmsg:v1:abc",
      "kaether:role:v1:abc",
      "kaether:gmember:v1:abc",
      "kaether:call:revoke:v1:abc",
      "kaether:call:link:v1:abc",
    ].map((s) => VoiceMessageMetadataCodec.isVoiceMessage(s));
    if (!positive) return fail(name, "returned false for a valid voice message payload");
    const falsePositive = negatives.findIndex((v) => v);
    if (falsePositive !== -1) return fail(name, `false positive at index ${falsePositive}`);
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testDurationBelowMinRejected(): Promise<TestCase> {
  const name = "duration below 200ms is rejected on encode";
  try {
    const m = makeVoiceMessageMetadata({ durationMillis: 199, waveformBuckets: makeWaveform() });
    VoiceMessageMetadataCodec.encode(m);
    return fail(name, "encode accepted 199ms");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidDuration" &&
      e.cause.durationMillis === 199
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testDurationAboveMaxRejected(): Promise<TestCase> {
  const name = "duration above 180_000ms is rejected on encode";
  try {
    const m = makeVoiceMessageMetadata({ durationMillis: 180_001, waveformBuckets: makeWaveform() });
    VoiceMessageMetadataCodec.encode(m);
    return fail(name, "encode accepted 180_001ms");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidDuration" &&
      e.cause.durationMillis === 180_001
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testSampleRateNot16kRejected(): Promise<TestCase> {
  const name = "sample rate other than 16000 is rejected";
  try {
    const m = makeVoiceMessageMetadata({
      durationMillis: 1000,
      sampleRateHz: 44_100,
      waveformBuckets: makeWaveform(),
    });
    VoiceMessageMetadataCodec.encode(m);
    return fail(name, "encode accepted 44100Hz");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidSampleRate" &&
      e.cause.sampleRateHz === 44_100
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testBitRateOutOfRangeRejected(): Promise<TestCase> {
  const name = "bit rate outside 16k-64k is rejected";
  try {
    const m = makeVoiceMessageMetadata({
      durationMillis: 1000,
      bitRateBps: 4_000,
      waveformBuckets: makeWaveform(),
    });
    VoiceMessageMetadataCodec.encode(m);
    return fail(name, "encode accepted 4000bps");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidBitRate" &&
      e.cause.bitRateBps === 4_000
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testWaveformLengthRejected(): Promise<TestCase> {
  const name = "waveform != 64 bytes is rejected on encode";
  try {
    const m = makeVoiceMessageMetadata({
      durationMillis: 1000,
      waveformBuckets: new Uint8Array(32), // wrong length
    });
    VoiceMessageMetadataCodec.encode(m);
    return fail(name, "encode accepted 32-byte waveform");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidWaveformLength" &&
      e.cause.length === 32
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema is rejected on decode";
  // Hand-craft a payload with schema=99 by crafting raw JSON and encoding it.
  const waveformB64 = btoa(
    String.fromCharCode(...new Uint8Array(64).fill(0)),
  );
  const json = JSON.stringify({
    schema: 99,
    durationMillis: 1000,
    sampleRateHz: 16_000,
    bitRateBps: 32_000,
    waveformBuckets: waveformB64,
    recordedAt: 1,
  });
  const payload = VoiceMessageMetadataCodec.PREFIX + b64url(json);
  try {
    VoiceMessageMetadataCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "unsupportedSchema" &&
      e.cause.schema === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testMalformedBase64Rejected(): Promise<TestCase> {
  const name = "malformed base64 is rejected on decode";
  const bad = VoiceMessageMetadataCodec.PREFIX + "@@@not-base64@@@";
  try {
    VoiceMessageMetadataCodec.decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof VoiceMessageMetadataError && e.cause.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

/**
 * Decode-side validation: maliciously crafted wire payload with a valid
 * prefix but bad duration — must be rejected even though the prefix is
 * correct. This verifies that decode does not trust the wire.
 */
async function testDecodeSideValidationRejectsBadDuration(): Promise<TestCase> {
  const name = "decode-side validation rejects maliciously crafted bad-duration payload";
  // Encode a valid message first, then tamper the JSON to have duration=0.
  const waveformB64 = btoa(String.fromCharCode(...new Uint8Array(64).fill(0)));
  const json = JSON.stringify({
    schema: 1,
    durationMillis: 0, // too short — would be rejected by validate()
    sampleRateHz: 16_000,
    bitRateBps: 32_000,
    waveformBuckets: waveformB64,
    recordedAt: 1_700_000_000,
  });
  const payload = VoiceMessageMetadataCodec.PREFIX + b64url(json);
  try {
    VoiceMessageMetadataCodec.decode(payload);
    return fail(name, "decode accepted a payload with durationMillis=0");
  } catch (e) {
    if (
      e instanceof VoiceMessageMetadataError &&
      e.cause.kind === "invalidDuration"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// B. Waveform downsampling
// ---------------------------------------------------------------------------

async function testWaveformEmptyInput(): Promise<TestCase> {
  const name = "empty samples → 64 zero bytes";
  const out = VoiceMessageWaveform.downsample([]);
  if (out.length !== 64) return fail(name, `length=${out.length}`);
  const nonZero = Array.from(out).filter((b) => b !== 0).length;
  if (nonZero !== 0) return fail(name, `${nonZero} non-zero buckets`);
  return pass(name);
}

async function testWaveformConstantInput(): Promise<TestCase> {
  const name = "constant 0.5 input → all 255 normalised buckets";
  const samples = new Array<number>(1024).fill(0.5);
  const out = VoiceMessageWaveform.downsample(samples);
  if (out.length !== 64) return fail(name, `length=${out.length}`);
  const wrong = Array.from(out).filter((b) => b !== 255).length;
  if (wrong !== 0)
    return fail(name, `${wrong} buckets not 255 (first=${out[0]})`);
  return pass(name);
}

async function testWaveformSineProducesNonDegenerate(): Promise<TestCase> {
  const name = "sine input → non-degenerate (>1 unique) buckets";
  // 1Hz envelope × 220Hz carrier over 16000 samples, as in the iOS test.
  const samples: number[] = [];
  for (let i = 0; i < 16_000; i++) {
    const t = i / 16_000.0;
    const env = Math.sin(2 * Math.PI * 1.0 * t);
    const carrier = Math.sin(2 * Math.PI * 220.0 * t);
    samples.push(env * carrier);
  }
  const out = VoiceMessageWaveform.downsample(samples);
  if (out.length !== 64) return fail(name, `length=${out.length}`);
  const unique = new Set(out).size;
  if (unique <= 1) return fail(name, `only ${unique} unique bucket values`);
  return pass(name);
}

async function testWaveformBucketCountEnforced(): Promise<TestCase> {
  const name = "downsample output is always exactly 64 bytes";
  const inputSizes = [10, 64, 100, 1000, 16_000, 48_000];
  for (const n of inputSizes) {
    const samples = new Array<number>(n).fill(0.25);
    const out = VoiceMessageWaveform.downsample(samples);
    if (out.length !== 64) {
      return fail(name, `n=${n} gave length=${out.length}`);
    }
  }
  return pass(name);
}

async function testWaveformUpscalesShortInput(): Promise<TestCase> {
  const name = "fewer samples than buckets upscales to exactly 64 bytes";
  const samples = [0.1, 0.5, 0.9];
  const out = VoiceMessageWaveform.downsample(samples);
  if (out.length !== 64) return fail(name, `length=${out.length}`);
  return pass(name);
}

async function testWaveformDeterministic(): Promise<TestCase> {
  const name = "downsampling is deterministic (same input → same output)";
  const samples: number[] = [];
  for (let i = 0; i < 2048; i++) {
    samples.push(Math.sin(i * 0.05));
  }
  const a = VoiceMessageWaveform.downsample(samples);
  const b = VoiceMessageWaveform.downsample(samples);
  for (let i = 0; i < 64; i++) {
    if (a[i] !== b[i]) return fail(name, `a[${i}]=${a[i]} != b[${i}]=${b[i]}`);
  }
  return pass(name);
}

/**
 * Known-vector test: constant amplitude 0.5 over 128 samples → all 255.
 * This is the simplest deterministic vector where the expected output is
 * analytically provable:
 *   - Every bucket covers equal-amplitude samples → all RMS values are equal.
 *   - Normalisation maps the peak (= any bucket value) to 255.
 *   - Therefore every output byte = 255.
 */
async function testWaveformKnownVector(): Promise<TestCase> {
  const name = "known PCM vector produces expected 64-byte RMS output";
  const samples = new Array<number>(128).fill(0.5);
  const out = VoiceMessageWaveform.downsample(samples);
  if (out.length !== 64) return fail(name, `length=${out.length}`);
  for (let i = 0; i < 64; i++) {
    if (out[i] !== 255)
      return fail(name, `bucket[${i}]=${out[i]}, expected 255`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// C. Silence ratio
// ---------------------------------------------------------------------------

async function testSilenceRatioAllZero(): Promise<TestCase> {
  const name = "all-zero samples → silenceRatio = 1.0";
  const r = VoiceMessageWaveform.silenceRatio(new Array<number>(1000).fill(0));
  if (r !== 1.0) return fail(name, `got ${r}`);
  return pass(name);
}

async function testSilenceRatioAllLoud(): Promise<TestCase> {
  const name = "all-loud samples → silenceRatio = 0.0";
  const r = VoiceMessageWaveform.silenceRatio(new Array<number>(1000).fill(0.9));
  if (r !== 0.0) return fail(name, `got ${r}`);
  return pass(name);
}

async function testSilenceRatioMixed(): Promise<TestCase> {
  const name = "mixed samples → silenceRatio ≈ 0.25";
  // 250 silent + 750 loud → 0.25
  const samples = [
    ...new Array<number>(250).fill(0.0),
    ...new Array<number>(750).fill(0.9),
  ];
  const r = VoiceMessageWaveform.silenceRatio(samples);
  if (r <= 0 || r >= 1 || Math.abs(r - 0.25) > 0.001)
    return fail(name, `got ${r}`);
  return pass(name);
}

// ---------------------------------------------------------------------------
// D. Recording policy
// ---------------------------------------------------------------------------

async function testPolicyContinueBelowMax(): Promise<TestCase> {
  const name = "elapsed just under max → continueRecording";
  const d = VoiceMessageRecordingPolicy.decide({
    elapsedMillis: 179_999,
    consecutiveSilenceMillis: 0,
    userPaused: false,
  });
  if (d !== "continueRecording") return fail(name, `got ${d}`);
  return pass(name);
}

async function testPolicyForceStopAtMax(): Promise<TestCase> {
  const name = "elapsed at max → forceStopMaxDuration";
  const d = VoiceMessageRecordingPolicy.decide({
    elapsedMillis: 180_000,
    consecutiveSilenceMillis: 0,
    userPaused: false,
  });
  if (d !== "forceStopMaxDuration") return fail(name, `got ${d}`);
  return pass(name);
}

async function testPolicyForceStopOnSilenceCap(): Promise<TestCase> {
  const name = "consecutive silence at cap → forceStopSilence";
  const d = VoiceMessageRecordingPolicy.decide({
    elapsedMillis: 1_000,
    consecutiveSilenceMillis: 5_000,
    userPaused: false,
  });
  if (d !== "forceStopSilence") return fail(name, `got ${d}`);
  return pass(name);
}

async function testPolicyUserPausedShortCircuits(): Promise<TestCase> {
  const name = "userPaused short-circuits → forceStopUserPaused";
  const d = VoiceMessageRecordingPolicy.decide({
    elapsedMillis: 1_000,
    consecutiveSilenceMillis: 0,
    userPaused: true,
  });
  if (d !== "forceStopUserPaused") return fail(name, `got ${d}`);
  return pass(name);
}

async function testPolicyUserPausedBeatsMaxDuration(): Promise<TestCase> {
  const name = "userPaused beats max-duration in priority";
  // Even at (and beyond) the duration cap, explicit pause wins.
  const d = VoiceMessageRecordingPolicy.decide({
    elapsedMillis: 999_999,
    consecutiveSilenceMillis: 999_999,
    userPaused: true,
  });
  if (d !== "forceStopUserPaused") return fail(name, `got ${d}`);
  return pass(name);
}

// ---------------------------------------------------------------------------
// E. Playback-rate store
// ---------------------------------------------------------------------------

async function testRateDefaultIsX1(): Promise<TestCase> {
  const name = "default playback rate is 1.0x";
  const s = freshRateStore();
  const r = await s.getRate("ch-1");
  if (r !== 1.0) return fail(name, `got ${r}`);
  return pass(name);
}

async function testRateRoundTrip(): Promise<TestCase> {
  const name = "setRate then getRate round-trips (1.5x)";
  const s = freshRateStore();
  await s.setRate("ch-1", 1.5);
  const r = await s.getRate("ch-1");
  if (r !== 1.5) return fail(name, `got ${r}`);
  return pass(name);
}

async function testRatePerChannelIsolation(): Promise<TestCase> {
  const name = "playback rate is per-channel isolated";
  const s = freshRateStore();
  await s.setRate("ch-1", 2.0);
  await s.setRate("ch-2", 1.5);
  const r1 = await s.getRate("ch-1");
  const r2 = await s.getRate("ch-2");
  const r3 = await s.getRate("ch-3"); // never set → default 1.0
  if (r1 !== 2.0 || r2 !== 1.5 || r3 !== 1.0)
    return fail(name, `r1=${r1} r2=${r2} r3=${r3}`);
  return pass(name);
}

async function testRatePersistsAcrossInstances(): Promise<TestCase> {
  const name = "playback rate persists across store instances (write 1.5x, reload, get 1.5x)";
  const dbName = `kaether:voice-rate:selftest:persist:${Date.now()}`;
  const writer = new VoicePlaybackRateStore(dbName);
  await writer.setRate("ch-1", 1.5);
  // New instance pointing at the same IDB database.
  const reader = new VoicePlaybackRateStore(dbName);
  const r = await reader.getRate("ch-1");
  if (r !== 1.5) return fail(name, `got ${r}`);
  return pass(name);
}

async function testRateResetAll(): Promise<TestCase> {
  const name = "resetAll clears every channel rate";
  const s = freshRateStore();
  await s.setRate("ch-1", 2.0);
  await s.setRate("ch-2", 1.5);
  await s.resetAll();
  const r1 = await s.getRate("ch-1");
  const r2 = await s.getRate("ch-2");
  if (r1 !== 1.0 || r2 !== 1.0)
    return fail(name, `after resetAll: ch-1=${r1} ch-2=${r2}`);
  return pass(name);
}
